import { reportHandledException } from '@/lib/error-reporting'
import { storeToken } from '../../auth/auth-storage'
import { readReferralCode, clearReferralCode } from '../../referral/referral-storage'
import {
  trackAddPaymentInfo,
  trackPurchase,
  trackPurchaseFailed,
  collectPurchaseAttribution,
  type CommercePlan,
} from '../../../lib/analytics'
import { BillingApiError } from '../api-client'
import { billingErrorMessage } from '../billing-error-message'
import type { InvoiceSettings } from '../api-client'
import {
  outcomeFromChargeError,
  outcomeFromChargeResult,
  type ChargeResultShape,
} from './checkout-outcome'
import type { CheckoutOutcome, CheckoutStrategy } from './checkout-types'
import { TeamApiError, teamErrorKind, teamErrorMessageKey } from '../../team/api-client'
import { APP_ROUTES } from '../../../lib/app-routes'
import type { PersonalPaidPlanCode, SubscriptionCycle } from '@kiao-voice/core'

type Translate = (key: string, params?: Record<string, unknown>) => string

// Charge/bind API function types — referenced as injected deps (test seams)
// without importing the implementations as values.
type SubscribeFn = typeof import('../api-client').subscribeViaTappay
type GuestCheckoutFn = typeof import('../api-client').guestCheckoutViaTappay
type PurchasePackFn = typeof import('../api-client').purchaseCreditPackViaTappay
type WalletTopUpFn = typeof import('../api-client').topUpWalletViaTappay
type BindCardFn = typeof import('../api-client').bindCard
type SubscribeTeamFn = typeof import('../../team/api-client').subscribeTeam

/**
 * Fires the commerce analytics implied by a settled outcome — the single place
 * the funnel events are mapped, so every product reports them identically.
 * Mirrors the prior per-call-site logic: a Purchase only on a REAL charge
 * (tradeId present); purchase_failed on a decline or an unrecoverable error;
 * nothing on a 3DS redirect (the server owns that conversion) or on the
 * card_required / coupon_invalid control codes.
 */
function applyChargeAnalytics(
  outcome: CheckoutOutcome,
  plan: CommercePlan,
  eventId: string | undefined
): void {
  if (outcome.kind === 'success') {
    if (outcome.tradeId) {
      trackPurchase({ plan, transactionId: outcome.tradeId, eventId })
    }
  } else if (outcome.kind === 'declined') {
    trackPurchaseFailed({ plan, reason: 'declined' })
  } else if (outcome.kind === 'error') {
    trackPurchaseFailed({ plan, reason: 'error' })
  }
}

type PurchaseAttribution = ReturnType<typeof collectPurchaseAttribution>

/**
 * The shared charge core for the simple authed flows (subscribe / credit-pack):
 * fire add_payment_info, collect attribution once, call the product's API, then
 * map the result/error to a {@link CheckoutOutcome} and fire its funnel
 * analytics. Keeping this in ONE place is the whole point — the prior per-call
 * duplication is exactly what let an analytics/error-mapping change drift
 * between surfaces. (Guest checkout has extra requiresSignIn + token + referral
 * steps, so it stays explicit.)
 */
async function settleCharge(
  plan: () => CommercePlan,
  fallbackKey: string,
  t: Translate,
  call: (attribution: PurchaseAttribution) => Promise<ChargeResultShape>
): Promise<CheckoutOutcome> {
  trackAddPaymentInfo(plan())
  const attribution = collectPurchaseAttribution()
  try {
    const result = await call(attribution)
    const outcome = outcomeFromChargeResult(result, t, fallbackKey)
    applyChargeAnalytics(outcome, plan(), attribution.eventId)
    return outcome
  } catch (cause) {
    reportHandledException(cause, { source: 'features/billing/checkout/checkout-strategies.ts' })
    const outcome = outcomeFromChargeError(cause, t, fallbackKey)
    applyChargeAnalytics(outcome, plan(), attribution.eventId)
    return outcome
  }
}

interface SubscribeStrategyDeps {
  planCode: PersonalPaidPlanCode
  cycle: SubscriptionCycle
  amount: number
  subscribeFn: SubscribeFn
  getTokenFn: () => string | null
  t: Translate
}

/** Authed subscribe via `/billing/subscribe`. */
export function createSubscribeStrategy({
  planCode,
  cycle,
  amount,
  subscribeFn,
  getTokenFn,
  t,
}: SubscribeStrategyDeps): CheckoutStrategy {
  const plan = (): CommercePlan => ({ code: planCode, name: planCode, priceTwd: amount, cycle })
  return {
    async submit({ prime, cardholder, couponCode, checkoutAttemptId }) {
      if (!getTokenFn()) return { kind: 'noop' }
      return settleCharge(plan, 'billing.errorSubscribe', t, (a) =>
        subscribeFn({
          prime,
          planCode,
          cycle,
          couponCode,
          cardholder,
          remember: true,
          checkoutAttemptId,
          eventId: a.eventId,
          fbp: a.fbp,
          fbc: a.fbc,
          gaClientId: a.gaClientId,
        })
      )
    },
  }
}

interface TeamSubscribeStrategyDeps {
  seats: number
  cycle: SubscriptionCycle
  name: string
  amount: number
  subscribeTeamFn: SubscribeTeamFn
  getTokenFn: () => string | null
  getInvoiceSettings: () => InvoiceSettings | null
  t: Translate
}

/** Authed Team subscribe via `/billing/team/subscribe`. */
export function createTeamSubscribeStrategy({
  seats,
  cycle,
  name,
  amount,
  subscribeTeamFn,
  getTokenFn,
  getInvoiceSettings,
  t,
}: TeamSubscribeStrategyDeps): CheckoutStrategy {
  const plan = (): CommercePlan => ({ code: 'team', name: 'team', priceTwd: amount, cycle })
  return {
    async submit({ prime, cardholder, couponCode, checkoutAttemptId }) {
      if (!getTokenFn()) return { kind: 'noop' }
      // A live trial is converted by buying seats on the team page, never by a
      // second subscription; the refusal names that page instead of a retry.
      let trialActive = false
      const outcome = await settleCharge(plan, 'team.errGeneric', t, async () => {
        try {
          return await subscribeTeamFn({
            prime,
            seats,
            cycle,
            name,
            couponCode,
            cardholder,
            remember: true,
            checkoutAttemptId,
            invoiceSettings: getInvoiceSettings() ?? undefined,
          })
        } catch (cause) {
          if (cause instanceof TeamApiError) {
            trialActive = teamErrorKind(cause.code ?? '') === 'TrialActive'
            throw new BillingApiError(cause.status, cause.body, t(teamErrorMessageKey(cause.code)))
          }
          throw cause
        }
      })
      return trialActive && outcome.kind === 'error'
        ? { ...outcome, action: { href: APP_ROUTES.team, label: t('team.errTrialActiveCta') } }
        : outcome
    },
  }
}

interface GuestCheckoutStrategyDeps {
  planCode: PersonalPaidPlanCode
  cycle: SubscriptionCycle
  amount: number
  guestCheckoutFn: GuestCheckoutFn
  t: Translate
}

/**
 * Token-less guest subscribe via `/billing/guest-checkout`. The cardholder email
 * becomes the account email: the server find-or-creates and charges, or reports
 * the email already has an account → pivot to OTP (`requiresSignIn`, no charge; with
 * `paid`, charged but signed in through the mailed link instead).
 */
export function createGuestCheckoutStrategy({
  planCode,
  cycle,
  amount,
  guestCheckoutFn,
  t,
}: GuestCheckoutStrategyDeps): CheckoutStrategy {
  const plan = (): CommercePlan => ({ code: planCode, name: planCode, priceTwd: amount, cycle })
  return {
    async submit({ prime, cardholder, couponCode, checkoutAttemptId }) {
      // A guest always supplies a card (a new account has no wallet); the panel
      // never shows the card-free button for guest, so this is purely defensive.
      if (!prime) return { kind: 'noop' }
      trackAddPaymentInfo(plan())
      const attribution = collectPurchaseAttribution()
      try {
        const result = await guestCheckoutFn({
          email: cardholder.email,
          prime,
          planCode,
          cycle,
          couponCode,
          // Referral code captured from a `?ref=` link, if any (issue #71). The
          // server captures it only when it creates a brand-new account.
          referralCode: readReferralCode() ?? undefined,
          cardholder,
          remember: true,
          checkoutAttemptId,
          eventId: attribution.eventId,
          fbp: attribution.fbp,
          fbc: attribution.fbc,
          gaClientId: attribution.gaClientId,
        })
        // Delivered to the server (it owns the once-per-referee + only-on-new
        // capture guards) — drop it so a later checkout can't re-send a stale code.
        clearReferralCode()
        if (result.requiresSignIn) {
          if (!result.paid) return { kind: 'requiresSignIn', email: cardholder.email }
          // Charged and settled, but no session for this device: the purchase is
          // real, so it is recorded like any other.
          applyChargeAnalytics(
            { kind: 'success', tradeId: result.tradeId },
            plan(),
            attribution.eventId
          )
          return { kind: 'requiresSignIn', email: cardholder.email, paid: true }
        }
        const outcome = outcomeFromChargeResult(result, t, 'billing.errorSubscribe')
        // Persist the session for the paying device on a real new-account charge.
        if (outcome.kind === 'success' && result.token) {
          storeToken(result.token.value)
        }
        applyChargeAnalytics(outcome, plan(), attribution.eventId)
        return outcome
      } catch (cause) {
        reportHandledException(cause, {
          source: 'features/billing/checkout/checkout-strategies.ts',
        })
        const outcome = outcomeFromChargeError(cause, t, 'billing.errorSubscribe')
        applyChargeAnalytics(outcome, plan(), attribution.eventId)
        return outcome
      }
    },
  }
}

interface CreditPackStrategyDeps {
  packCode: string
  credits: number
  price: number
  purchasePackFn: PurchasePackFn
  getTokenFn: () => string | null
  t: Translate
}

/** Authed credit-pack purchase via `/billing/credit-packs`. */
export function createCreditPackStrategy({
  packCode,
  credits,
  price,
  purchasePackFn,
  getTokenFn,
  t,
}: CreditPackStrategyDeps): CheckoutStrategy {
  const plan = (): CommercePlan => ({
    code: `pack_${packCode}`,
    name: `Credit pack ${credits}`,
    priceTwd: price,
  })
  return {
    async submit({ prime, cardholder, couponCode, checkoutAttemptId }) {
      if (!getTokenFn()) return { kind: 'noop' }
      return settleCharge(plan, 'billing.errorPackBuy', t, (a) =>
        purchasePackFn({
          prime,
          checkoutAttemptId,
          packCode,
          couponCode,
          cardholder,
          // Remembered like every other purchase. The product is
          // subscription-first, so a buyer who came through a one-off pack is
          // very likely to charge again, and the saved card is what lets that
          // happen without re-entering it. TapPay only returns `card_secret`
          // when this is true, so it is also what makes the card exist at all.
          remember: true,
          eventId: a.eventId,
          fbp: a.fbp,
          fbc: a.fbc,
          gaClientId: a.gaClientId,
        })
      )
    },
  }
}

interface WalletTopUpStrategyDeps {
  amount: number
  topUpFn: WalletTopUpFn
  getTokenFn: () => string | null
  t: Translate
}

/**
 * Authed 儲值金 top-up via `/billing/wallet/top-up`. Always a card charge: you
 * can't fund a wallet from itself, so the panel passes `coupon: null` (no
 * coupon / wallet-offset / card-free path) and this strategy requires a prime.
 */
export function createWalletTopUpStrategy({
  amount,
  topUpFn,
  getTokenFn,
  t,
}: WalletTopUpStrategyDeps): CheckoutStrategy {
  const plan = (): CommercePlan => ({
    code: 'wallet_topup',
    name: `Wallet top-up ${amount}`,
    priceTwd: amount,
  })
  return {
    async submit({ prime, cardholder, checkoutAttemptId }) {
      if (!getTokenFn()) return { kind: 'noop' }
      if (!prime) return { kind: 'noop' }
      return settleCharge(plan, 'billing.walletTopUpError', t, (a) =>
        topUpFn({
          prime,
          amountTwd: amount,
          cardholder,
          // Same reasoning as the credit pack above.
          remember: true,
          checkoutAttemptId,
          eventId: a.eventId,
          fbp: a.fbp,
          fbc: a.fbc,
          gaClientId: a.gaClientId,
        })
      )
    },
  }
}

interface BindCardStrategyDeps {
  bindCardFn: BindCardFn
  getTokenFn: () => string | null
  t: Translate
}

/**
 * Add a card on file via `/billing/cards`. Not a purchase — no breakdown,
 * coupon, wallet, or analytics; always collects a card and reports success/error.
 */
export function createBindCardStrategy({
  bindCardFn,
  getTokenFn,
  t,
}: BindCardStrategyDeps): CheckoutStrategy {
  return {
    async submit({ prime, cardholder }) {
      const token = getTokenFn()
      if (!token) return { kind: 'noop' }
      // Binding a card always collects one — the card-free path never applies.
      if (!prime) return { kind: 'noop' }
      try {
        await bindCardFn({ prime, cardholder })
        return { kind: 'success' }
      } catch (cause) {
        reportHandledException(cause, {
          source: 'features/billing/checkout/checkout-strategies.ts',
        })
        return {
          kind: 'error',
          message: billingErrorMessage(cause, t, 'billing.errorBindCard'),
        }
      }
    },
  }
}
